'use client';

import { IProductDetail } from '@/api/types/public';
import Image from '@/components/Image';
import ImagePreview, { ImagePreviewRef } from '@/components/ImagePreview';
import SwiperNavigationButtons from '@/components/SwiperNavigation';
import { useClientOnly } from '@/hooks/useClientOnly';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import { colors } from '@/theme/zeelool.theme';
import { getSession } from '@/utils/tools';
import { Box, Typography } from '@mui/material';
import {
  CollapseArrowDownOutlined,
  CollapseArrowUpOutlined,
  WapCollapseUpFilled,
} from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { isChangeGreyImg } from '@/utils/goods-detail-utils';
import { createPortal } from 'react-dom';
import { useInView } from 'react-intersection-observer';
import { Swiper as SwiperType } from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { IImageListItem } from './handleData';
// CSS已合并到 globals.css，无需单独导入

const ZOOM_PREVIEW_BORDER_WIDTH = 1;

// MediaItem 组件用于处理单个媒体项（图片或视频）的渲染和曝光埋点
interface MediaItemProps {
  item: IImageListItem;
  index: number;
  productDetail: IProductDetail;
  videoRef?: React.RefObject<HTMLVideoElement>;
  imgArr: IImageListItem[];
  handleImageClick: () => void;
  isGlass: boolean;
  isWeb: boolean;
  isNewGoodsDetail: boolean;
}

const MediaItem: React.FC<MediaItemProps> = ({
  item,
  index,
  productDetail,
  videoRef,
  handleImageClick,
  imgArr = [],
  isGlass,
  isWeb,
  isNewGoodsDetail,
}) => {
  const { trackEvent } = useReporting();

  // 使用 useInView 监听曝光
  const { ref, inView } = useInView({
    threshold: 0.8, // 50% 可见时触发
    triggerOnce: true, // 只触发一次
  });

  // 曝光埋点
  useEffect(() => {
    if (inView) {
      trackEvent(
        {
          eventName: 'ProductDetailsMainPic_View',
          properties: {
            big_picture_url: item?.url,
            big_picture_index: index,
            shop_sku: productDetail.shop_sku,
            business_sku: productDetail.business_sku,
            product_sku: productDetail.sku,
            product_id: `${productDetail.id}`,
            product_name: productDetail.short_name,
          },
        },
        [ProviderType.SENSORS],
      );
    }
  }, [inView]);

  // 渲染视频
  if (item.goodsType === 'video') {
    return (
      <div ref={ref} className="w-full h-full">
        <video
          ref={videoRef}
          className="w-full h-full wapvideo"
          controls
          playsInline
          controlsList="nodownload noplaybackrate noremoteplayback"
          disablePictureInPicture
          loop
          preload="auto"
          muted
          onClick={(e) => e.stopPropagation()}
        >
          <source src={item.url} />
        </video>
      </div>
    );
  }

  // 首张图片设置 priority 以优化 LCP
  const isFirstImage = index === 0 && item.goodsType !== 'video';

  // 根据实际显示大小优化图片尺寸（LCP 优化）
  // Web端最大显示 38.3vw，移动端全屏显示
  // 使用更大的尺寸以确保高分辨率显示，但不超过实际需要的2倍
  const imageWidth = isWeb ? 800 : 400; // Web端使用更大尺寸，移动端适中
  const imageHeight = imageWidth; // 保持1:1比例

  const imageProps = {
    src: item.url,
    width: imageWidth,
    height: imageHeight,
    priority: isFirstImage, // 首张图片优先加载
    fetchPriority: isFirstImage ? ('high' as const) : ('auto' as const), // 首张图片设置高优先级以优化 LCP
    loading: isFirstImage ? ('eager' as const) : ('lazy' as const), // 首张图片立即加载，其他懒加载
    quality: 100,
    className: `h-auto object-cover select-none
    ${isWeb ? 'max-w-[38.33333vw] max-h-[38.33333vw] w-[38.33333vw]' : 'max-w-full max-h-full'}
    ${isGlass && isWeb && ['product', 'product_gray'].includes(item.goodsType) ? '!w-[32.3958333333vw]' : ''}
    ${isGlass && isChangeGreyImg(item, imgArr) ? 'mix-blend-multiply' : ''}
    ${!isWeb && !item.goodsType.includes('product') ? 'w-screen' : ''}
    ${isWeb && !item.goodsType.includes('product') ? '!w-[38.3vw]' : ''}
   `,
  };
  const alt = productDetail?.title
    ? `${productDetail?.title}`
    : `Buy ${productDetail?.short_name} Optical Glasses and EyeWear Frames on Zeelool`;
  // 渲染图片 - 在Web商品详情页使用普通Image，因为悬浮功能在div层实现
  const wrapperClassNameM = () => {
    if (isNewGoodsDetail) {
      if (['product_detail'].includes(item.goodsType)) return 'px-0';
      if (['product', 'product_gray'].includes(item.goodsType)) return 'px-[28px]';
    }
    if (
      productDetail?.is_glass === 1 &&
      ['product', 'product_gray', 'product_detail'].includes(item.goodsType)
    ) {
      return 'px-[38px]';
    }
    return 'px-0';
  };
  return (
    <div
      onClick={() => handleImageClick()}
      ref={ref}
      className={`${!isWeb ? wrapperClassNameM() : ''}`}
    >
      <Image {...imageProps} alt={`${alt}${index}`} />
    </div>
  );
};

interface MainPictureProps {
  productDetail: IProductDetail;
  images: IImageListItem[];
  onTabTypeChange: (type: string) => void;
  showTabBtn?: boolean;
  isWeb?: boolean;
  isGoodsDetail?: boolean;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
}

const MainPicture = forwardRef<{ swiper: SwiperType }, MainPictureProps>(
  (
    {
      productDetail,
      images,
      onTabTypeChange,
      showTabBtn = false,
      isWeb = false,
      isGoodsDetail = false,
      SDProductDetailsPageBtnClick,
    },
    ref,
  ) => {
    const t = useTranslations('goodsDetail');

    const isWebGoodsDetail = useMemo(() => {
      return isWeb && isGoodsDetail;
    }, [isWeb, isGoodsDetail]);

    // Calculate integer width for container to prevent sub-pixel rendering issues in Swiper
    const [containerSize, setContainerSize] = useState<string | number>(
      isWebGoodsDetail ? 'round(38.3vw, 1px)' : '100%',
    );

    useEffect(() => {
      if (!isWebGoodsDetail) {
        setContainerSize('100%');
        return;
      }

      const updateSize = () => {
        // Calculate precise integer pixels (38.3vw)
        const width = Math.floor(window.innerWidth * 0.383);
        setContainerSize(width);
      };

      updateSize();
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }, [isWebGoodsDetail]);

    const inches = useStore((state) => state.inches);
    const [showDimensions, setShowDimensions] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
    const [isAtBeginning, setIsAtBeginning] = useState(true); // 是否在顶部
    const [isAtEnd, setIsAtEnd] = useState(false); // 是否在底部

    // Div 悬浮预览相关状态
    const [isDivHovered, setIsDivHovered] = useState(false);
    const [forceUpdate, setForceUpdate] = useState(0); // 用于强制更新预览窗口位置
    const { trackEvent } = useReporting();
    const isClient = useClientOnly();

    // 视频播放埋点
    const sdProductVideoPlay = (videoUrl: string) => {
      trackEvent(
        {
          eventName: 'Video_Play',
          properties: {
            video_url: videoUrl,
            shop_sku: productDetail.shop_sku,
            business_sku: productDetail.business_sku,
            product_sku: productDetail.sku,
            product_id: `${productDetail.id}`,
            product_name: productDetail.short_name,
          },
        },
        [ProviderType.SENSORS],
      );
    };

    // 视频暂停埋点
    const sdProductVideoPause = (videoUrl: string) => {
      trackEvent(
        {
          eventName: 'Video_Pause',
          properties: {
            video_url: videoUrl,
            shop_sku: productDetail.shop_sku,
            business_sku: productDetail.business_sku,
            product_sku: productDetail.sku,
            product_id: `${productDetail.id}`,
            product_name: productDetail.short_name,
          },
        },
        [ProviderType.SENSORS],
      );
    };

    // 视频播放事件处理
    const handleVideoPlay = () => {
      const currentVideoItem = images.find((item) => item.goodsType === 'video');
      if (currentVideoItem) {
        sdProductVideoPlay(currentVideoItem.url);
      }
    };

    // 视频暂停事件处理
    const handleVideoPause = () => {
      const currentVideoItem = images.find((item) => item.goodsType === 'video');
      if (currentVideoItem) {
        sdProductVideoPause(currentVideoItem.url);
      }
    };

    // 为视频添加事件监听器
    useEffect(() => {
      const video = videoRef.current;
      if (video) {
        video.addEventListener('play', handleVideoPlay);
        video.addEventListener('pause', handleVideoPause);

        // 清理事件监听器
        return () => {
          video.removeEventListener('play', handleVideoPlay);
          video.removeEventListener('pause', handleVideoPause);
        };
      }
    }, [images, productDetail]); // 依赖项包含可能变化的数据
    const [divMousePosition, setDivMousePosition] = useState({ x: 0, y: 0 });
    const [divRect, setDivRect] = useState<DOMRect | null>(null);

    // Div 悬浮事件处理
    const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
      const currentItem = images[activeIndex];
      // 只在非视频且是Web商品详情页时启用
      if (isWebGoodsDetail && currentItem?.goodsType !== 'video') {
        setIsDivHovered(true);
        const rect = e.currentTarget.getBoundingClientRect();
        setDivRect(rect);
        setDivMousePosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      }
    };

    const handleMouseLeave = () => {
      setIsDivHovered(false);
      setDivRect(null);
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDivHovered) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setDivMousePosition({ x, y });
      setDivRect(rect);
    };

    // 选框和放大图共享主图的实际显示尺寸，不依赖大图的原始分辨率或加载时机。
    const initZoomDimensions = () => {
      if (!mainImageRef.current || !divRect || divRect.width <= 0 || divRect.height <= 0)
        return null;
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
      const hoverSize = Math.min(viewportWidth / 9.6, divRect.width, divRect.height);
      // border-box 的边框不属于右侧实际可见的图片区域。
      const zoomContentSize = getZoomWindowSize() - ZOOM_PREVIEW_BORDER_WIDTH * 2;

      return {
        hoverWidth: hoverSize,
        hoverHeight: hoverSize,
        pWidth: divRect.width,
        pHeight: divRect.height,
        bWidth: zoomContentSize,
        bHeight: zoomContentSize,
      };
    };

    // 选框以鼠标为中心，仅在主图边缘限制位置。
    const getHoverBoxPosition = () => {
      if (!divRect) return { left: 0, top: 0 };

      const dimensions = initZoomDimensions();
      if (!dimensions) return { left: 0, top: 0 };

      const { hoverWidth, hoverHeight, pWidth, pHeight } = dimensions;

      let left = divMousePosition.x - hoverWidth / 2;
      let top = divMousePosition.y - hoverHeight / 2;

      const maxWidth = pWidth - hoverWidth;
      const maxHeight = pHeight - hoverHeight;

      // 边界限制，参考Vue版本
      if (left < 0) {
        left = 0;
      } else if (left > maxWidth) {
        left = maxWidth;
      }

      if (top < 0) {
        top = 0;
      } else if (top > maxHeight) {
        top = maxHeight;
      }

      return { left, top };
    };

    // 获取Div悬浮预览窗口的容器样式
    const getDivZoomStyle = () => {
      return {
        position: 'relative' as const,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      };
    };

    // 计算预览窗口尺寸 - 统一的计算逻辑
    const getZoomWindowSize = () => {
      if (!isClient) return 736; // 服务端渲染时使用最大尺寸
      const maxImageSize = 736; // 最大尺寸限制
      const viewportBasedSize = window.innerWidth * 0.383; // 38.3vw
      return Math.min(maxImageSize, viewportBasedSize);
    };

    // 获取Div悬浮预览窗口的位置 - 跟随页面滚动同步更新
    const getDivZoomWindowPosition = () => {
      if (!isClient) return { display: 'none' };

      // 获取最新的主图元素位置信息，确保跟随滚动
      const currentRect = mainImageRef.current?.getBoundingClientRect();
      if (!currentRect) return { display: 'none' };

      // 计算预览窗口的垂直居中位置 - 基于最新的元素位置

      return {
        position: 'absolute' as const,
        right: `-100%`,
        top: `0`,
        zIndex: 3,
      };
    };

    // 获取Div悬浮指示器位置（参考Vue版本实现）
    const getDivIndicatorPosition = () => {
      const dimensions = initZoomDimensions();
      if (!dimensions) return {};

      const { left, top } = getHoverBoxPosition();

      return {
        position: 'absolute' as const,
        width: `${dimensions.hoverWidth}px`,
        height: `${dimensions.hoverHeight}px`,
        left: `0`,
        top: `0`,
        transform: `translate(${left}px, ${top}px)`,
        cursor: 'move',
        pointerEvents: 'auto' as const,
        zIndex: 10,
        userSelect: 'none' as const,
      };
    };

    const swiperRef = useRef<SwiperType | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const imagePreviewRef = useRef<ImagePreviewRef>(null);
    const mainImageRef = useRef<HTMLDivElement>(null);

    const convertToNumber = (value: string | number | undefined): number => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string') return parseFloat(value) || 0;
      return 0;
    };

    const frameSize = useMemo(
      () => ({
        frame_total_length: inches
          ? (convertToNumber(productDetail.frame_total_length) * 0.03937).toFixed(2)
          : productDetail.frame_total_length,
        frame_bridge: inches
          ? (convertToNumber(productDetail.frame_bridge) * 0.03937).toFixed(2)
          : productDetail.frame_bridge,
        glass_width: inches
          ? (convertToNumber(productDetail.glass_width) * 0.03937).toFixed(2)
          : productDetail.glass_width,
        glass_height: inches
          ? (convertToNumber(productDetail.glass_height) * 0.03937).toFixed(2)
          : productDetail.glass_height,
        frame_leg_length: inches
          ? (convertToNumber(productDetail.frame_leg_length) * 0.03937).toFixed(2)
          : productDetail.frame_leg_length,
      }),
      [inches, productDetail],
    );

    const handleSlideChange = useCallback(() => {
      if (!swiperRef.current) return;
      const { activeIndex: _activeIndex } = swiperRef.current;
      setActiveIndex(_activeIndex);

      const activeItem = images[_activeIndex];
      if (!activeItem) return;

      if (activeItem.type === 'Product') {
        onTabTypeChange('left');
      } else {
        onTabTypeChange('right');
      }

      if (videoRef.current) {
        if (activeItem.goodsType === 'video') {
          videoRef.current.play();
        } else {
          videoRef.current.pause();
        }
      }

      // 同步缩略图
      if (thumbsSwiper && thumbsSwiper !== swiperRef.current) {
        thumbsSwiper.slideTo(_activeIndex);
      }
    }, [images, onTabTypeChange, thumbsSwiper]);

    // 缩略图点击处理
    const handleThumbClick = useCallback((index: number) => {
      setActiveIndex(index);
      swiperRef.current?.slideTo(index, 0);
    }, []);

    const handleImageClick = (image: any) => {
      if (image.goodsType === 'video' || isWeb) return;
      SDProductDetailsPageBtnClick?.('enlarge pic');
      const imageList = images.filter((img) => img.goodsType !== 'video');
      const index = imageList.findIndex((img) => img.url === image.url);
      imagePreviewRef.current?.handleVisibleChange(index);
    };

    // 获取图片在产品数组中的索引
    const getProductImageIndex = (imageItem: any) => {
      const productImages = productDetail?.image?.product || [];
      const productGrayImages = (productDetail?.image as any)?.product_gray || [];

      if (Array.isArray(productGrayImages) && productGrayImages.length > 0) {
        return productGrayImages.findIndex((el: any) => el.url === imageItem.url);
      }
      return Array.isArray(productImages)
        ? productImages.findIndex((el: any) => el.url === imageItem.url)
        : -1;
    };

    // 检查图片位置是否匹配
    const checkImagePosition = (imageItem: any, positionValue: string | number | null) => {
      if (!positionValue) return false;
      const imageIndex = getProductImageIndex(imageItem);
      return imageIndex !== -1 && imageIndex + 1 === Number(positionValue);
    };

    // 是否为正视图
    const isFrontViewImage = (item: any) => {
      const frontViewPosition = productDetail?.extension_field?.front_view_position || null;
      return checkImagePosition(item, frontViewPosition);
    };

    // 是否为侧视图
    const isSideViewImage = (item: any) => {
      const profilePosition = productDetail?.extension_field?.profile_position || null;
      return checkImagePosition(item, profilePosition);
    };

    useEffect(() => {
      // !先用onAfterInit试一下，再看要不要废弃这段代码
      // const prePageImgSrc = getSession('zl-goods-detail-img-src');
      // if (prePageImgSrc) {
      //   const index = images.findIndex((item) => item.url === prePageImgSrc);
      //   if (index !== -1) {
      //     // 使用 setTimeout 确保 Swiper 完全初始化后再执行
      //     const timer = setTimeout(() => {
      //       if (swiperRef.current) {
      //         swiperRef.current.slideTo(index);
      //         // 手动同步所有相关状态
      //         setActiveIndex(index);
      //         const activeItem = images[index];
      //         // 同步tab状态
      //         if (activeItem.type === 'Product') {
      //           onTabTypeChange('left');
      //         } else {
      //           onTabTypeChange('right');
      //         }
      //         // 处理视频播放/暂停
      //         if (videoRef.current) {
      //           if (activeItem.goodsType === 'video') {
      //             videoRef.current.play();
      //           } else {
      //             videoRef.current.pause();
      //           }
      //         }
      //         // 同步缩略图
      //         if (thumbsSwiper && thumbsSwiper !== swiperRef.current) {
      //           thumbsSwiper.slideTo(index);
      //         }
      //       }
      //     }, 100);
      //     return () => clearTimeout(timer);
      //   }
      // }
      // return () => {
      //   removeSession('zl-goods-detail-img-src');
      // };
    }, [images, onTabTypeChange, thumbsSwiper]);

    // 添加滚动事件监听器 - 确保预览窗口跟随页面滚动
    useEffect(() => {
      if (!isDivHovered) return;

      let rafId: number;

      const handleScroll = () => {
        // 使用 requestAnimationFrame 节流，提高性能
        if (rafId) cancelAnimationFrame(rafId);

        rafId = requestAnimationFrame(() => {
          // 通过更新状态来触发重新渲染，确保预览窗口位置同步
          setForceUpdate((prev) => prev + 1);
        });
      };

      // 监听页面滚动
      window.addEventListener('scroll', handleScroll, { passive: true });

      // 清理事件监听器
      return () => {
        window.removeEventListener('scroll', handleScroll);
        if (rafId) cancelAnimationFrame(rafId);
      };
    }, [isDivHovered]);

    useImperativeHandle(ref, () => ({
      swiper: swiperRef.current as SwiperType,
    }));
    const initSwiper = (swiper: SwiperType) => {
      const prePageImgSrc = getSession('zl-goods-detail-img-src');
      if (prePageImgSrc) {
        const index = images.findIndex((item) => item.url === prePageImgSrc);
        if (index !== -1) {
          // 使用 setTimeout 确保 Swiper 完全初始化后再执行
          swiper.slideTo(index);

          // 手动同步所有相关状态
          setActiveIndex(index);

          const activeItem = images[index];

          // 同步tab状态
          if (activeItem.type === 'Product') {
            onTabTypeChange('left');
          } else {
            onTabTypeChange('right');
          }

          // 处理视频播放/暂停
          if (videoRef.current) {
            if (activeItem.goodsType === 'video') {
              videoRef.current.play();
            } else {
              videoRef.current.pause();
            }
          }

          // 同步缩略图
          if (thumbsSwiper && thumbsSwiper !== swiperRef.current) {
            thumbsSwiper.slideTo(index);
          }
        }
      }
    };
    const handleSwiper = (swiper: SwiperType) => {
      swiperRef.current = swiper;
    };
    const isGlass = useMemo(() => {
      return productDetail.is_glass === 1;
    }, [productDetail]);
    // 是否展示放大悬浮框
    const isShowZoomPop =
      isClient && isDivHovered && isWebGoodsDetail && images[activeIndex]?.goodsType !== 'video';
    return (
      <div className={`bg-white relative select-none ${isWebGoodsDetail ? 'flex gap-[16px]' : ''}`}>
        {/* 左侧缩略图swiper */}
        {isWebGoodsDetail ? (
          <div className="w-[72px] flex-shrink-0 relative">
            <Swiper
              direction="vertical"
              slidesPerView="auto"
              spaceBetween={8}
              modules={[Navigation]}
              navigation={{
                nextEl: '.main-picture-thumb-next',
                prevEl: '.main-picture-thumb-prev',
              }}
              onAfterInit={initSwiper}
              onSwiper={(swiper) => {
                setThumbsSwiper(swiper);
                // 初始化状态
                setIsAtBeginning(swiper.isBeginning);
                setIsAtEnd(swiper.isEnd);
              }}
              onReachBeginning={() => setIsAtBeginning(true)}
              onReachEnd={() => setIsAtEnd(true)}
              onFromEdge={() => {
                setIsAtBeginning(false);
                setIsAtEnd(false);
              }}
              onProgress={(swiper) => {
                setIsAtBeginning(swiper.isBeginning);
                setIsAtEnd(swiper.isEnd);
              }}
              className="h-[38vw]"
            >
              {images.map((item, index) => (
                <SwiperSlide key={item.url} className="!h-[72px]">
                  <div
                    className={`
                    w-[72px] h-[72px] rounded-6 overflow-hidden cursor-pointer border-1 transition-all bg-[#eeeeee]  flex items-center justify-center
                    ${index === activeIndex ? 'border-[#808080]' : 'border-transparent hover:border-[#a9a9a9]'}
                    ${['product', 'product_gray'].includes(item.goodsType) && isGlass ? 'p-[.3645833333vw]' : ''}
                  `}
                    onClick={() => {
                      SDProductDetailsPageBtnClick?.('enlarge pic');
                      handleThumbClick(index);
                    }}
                  >
                    {/* 普通图片 */}
                    {item.goodsType !== 'video' && (
                      <Image
                        src={item.url}
                        alt={`${productDetail?.name || 'Product'}${index}`}
                        width={72}
                        height={72}
                        quality={100}
                        className={`max-w-full max-h-full object-contain select-none ${isChangeGreyImg(item, images) && isGlass ? 'mix-blend-multiply' : ''}`}
                      />
                    )}
                    {/* 默认视频元素 */}
                    {item.goodsType === 'video' && (
                      <div className="relative w-full h-full">
                        <video
                          className="w-full h-full object-cover select-none"
                          preload="metadata"
                          muted
                        >
                          <source src={item.url} />
                        </video>
                        {/* 视频播放图标 */}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <WapCollapseUpFilled className="text-white text-24 rotate-90" />
                        </div>
                      </div>
                    )}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* 缩略图导航按钮 */}
            {!isAtBeginning && (
              <div
                className="main-picture-thumb-prev absolute top-0 left-1/2 -translate-x-1/2 z-10 w-[72px] h-[24px] bg-black/40 flex items-center justify-center cursor-pointer text-white text-16"
                onClick={() => thumbsSwiper?.slidePrev()}
              >
                <CollapseArrowUpOutlined />
              </div>
            )}
            {!isAtEnd && (
              <div
                className="main-picture-thumb-next absolute bottom-0 left-1/2 -translate-x-1/2 z-10 w-[72px] h-[24px] bg-black/40 flex items-center justify-center cursor-pointer text-white text-16"
                onClick={() => thumbsSwiper?.slideNext()}
              >
                <CollapseArrowDownOutlined />
              </div>
            )}
          </div>
        ) : null}
        {/* 右侧主图区域 */}
        <Box
          ref={mainImageRef}
          className={`${isWebGoodsDetail ? 'flex-none' : 'flex-1'} relative bg-[#eeeeee] swiper-nav-container goods-detail-main-picture aspect-square`}
          style={{
            width: containerSize,
            height: containerSize,
          }}
        >
          <Swiper
            modules={[Pagination, Navigation]}
            pagination={{
              type: 'fraction',
              el: '.custom-pagination',
            }}
            navigation={{
              prevEl: '.main-picture-prev',
              nextEl: '.main-picture-next',
            }}
            onAfterInit={initSwiper}
            onSlideChange={handleSlideChange}
            onSwiper={(swiper) => {
              handleSwiper(swiper);
              // 初始化主图边界状态
            }}
            className="w-full"
            style={(() => {
              const swiperStyle: React.CSSProperties & { [key: string]: string } = {
                '--swiper-navigation-color': 'transparent',
                width: '100%',
              };
              return swiperStyle;
            })()}
          >
            {images.map((item, index) => (
              <SwiperSlide key={item.url}>
                <Box
                  sx={{
                    width: '100%',
                    aspectRatio: '1/1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: item.goodsType === 'video' ? '#282828' : '#EEEEEE',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    className={`relative ${item.goodsType === 'video' ? 'w-full h-full' : ''}`}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onMouseMove={handleMouseMove}
                  >
                    <MediaItem
                      item={item}
                      index={index}
                      productDetail={productDetail}
                      videoRef={item.goodsType === 'video' ? videoRef : undefined}
                      handleImageClick={() => handleImageClick(item)}
                      isGlass={isGlass}
                      isWeb={isWeb}
                      imgArr={images}
                      isNewGoodsDetail={productDetail.product_structure_strategy_variable === 2}
                    />

                    {/* Div 悬浮指示器 */}
                    {isDivHovered && isWebGoodsDetail && item.goodsType !== 'video' && (
                      <div
                        // 拖动选框时不要启动轮播，否则 Swiper 会截获鼠标移动。
                        className="swiper-no-swiping bg-[url('/zeelool-next/goods-detail/dot.png')] bg-repeat"
                        style={getDivIndicatorPosition()}
                      />
                    )}

                    {/* Dimensions for front view */}
                    {isFrontViewImage(item) && showDimensions && (
                      <Box
                        className={`absolute inset-0  flex items-center justify-center aspect-[2/1] top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 ${isWeb ? 'w-full' : 'w-[calc(100%-28px)]'}`}
                      >
                        {/* Temple width */}
                        <Box className="absolute w-full text-center top-0">
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: isWeb ? '12px' : '10px',
                              position: 'absolute',
                              top: '-10px',
                              display: 'block',
                              width: '100%',
                            }}
                          >
                            {frameSize.frame_total_length}
                            {inches
                              ? t('mainPicture.units.inch')
                              : t('mainPicture.units.millimeter')}
                          </Typography>
                          <Image
                            src="/goods-detail/size_on_pic/temple_width.png"
                            alt="frame-width"
                            className="w-full h-auto select-none"
                            width={800}
                            height={100}
                            quality={100}
                          />
                        </Box>

                        {/* Lens width */}
                        <Box className="absolute left-[13%] top-1/2 -translate-y-1/2 w-[24.5%]">
                          <Box position="relative">
                            <Image
                              src="/goods-detail/size_on_pic/width.png"
                              alt="frame-width"
                              className="w-full h-auto select-none"
                              width={800}
                              height={100}
                              quality={100}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                fontSize: isWeb ? '12px' : '10px',
                              }}
                            >
                              {frameSize.glass_width}
                              {inches
                                ? t('mainPicture.units.inch')
                                : t('mainPicture.units.millimeter')}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Bridge width */}
                        <Box className="absolute bottom-[23.3%] left-1/2 -translate-x-1/2 w-[10%] flex items-center flex-col">
                          <Image
                            src="/goods-detail/size_on_pic/middle_width.png"
                            alt="frame-width"
                            className="w-full h-auto select-none"
                            width={800}
                            height={100}
                            quality={100}
                          />
                          <Typography
                            variant="caption"
                            align="center"
                            sx={{ display: 'block', fontSize: isWeb ? '12px' : '10px' }}
                          >
                            {frameSize.frame_bridge}
                            {inches
                              ? t('mainPicture.units.inch')
                              : t('mainPicture.units.millimeter')}
                          </Typography>
                        </Box>

                        {/* Lens height */}
                        <Box className="absolute right-[20.6%] top-1/2 -translate-y-1/2 w-[11%]">
                          <Box position="relative" height="100%">
                            <Image
                              src="/goods-detail/size_on_pic/height.png"
                              alt="frame-width"
                              className="w-full h-auto select-none"
                              width={800}
                              height={100}
                              quality={100}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                fontSize: isWeb ? '12px' : '10px',
                              }}
                            >
                              {frameSize.glass_height}
                              {inches
                                ? t('mainPicture.units.inch')
                                : t('mainPicture.units.millimeter')}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* Dimensions for side view */}
                    {isSideViewImage(item) && showDimensions && (
                      <Box
                        className={`absolute inset-0 flex items-center justify-center aspect-[2/1] top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 ${isWeb ? 'w-full' : 'w-[calc(100%-28px)]'}`}
                      >
                        <Box className="absolute left-[3.3%] top-[93%] w-[69.2%]">
                          <Image
                            src="/goods-detail/size_on_pic/leg_width.png"
                            alt="frame-width"
                            className="w-full h-auto select-none"
                            width={800}
                            height={100}
                            quality={100}
                          />
                          <Typography
                            variant="caption"
                            align="center"
                            sx={{ display: 'block', mt: '2px', fontSize: isWeb ? '12px' : '10px' }}
                          >
                            {frameSize.frame_leg_length}
                            {inches
                              ? t('mainPicture.units.inch')
                              : t('mainPicture.units.millimeter')}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </div>
                  {/* Show/Hide dimensions button */}
                  {isGlass && (isFrontViewImage(item) || isSideViewImage(item)) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: isWebGoodsDetail ? 'auto' : isGoodsDetail ? '16px' : '44px',
                        right: isWebGoodsDetail ? 'auto' : '14px',
                        bottom: isWebGoodsDetail ? '16px' : 'auto',
                        left: isWebGoodsDetail ? '24px' : 'auto',
                        fontSize: isWebGoodsDetail ? '14px' : '12px',
                        textDecoration: isWebGoodsDetail ? 'none' : 'underline !important',
                        color: colors.primaryText,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover': {
                          textDecoration: 'underline !important',
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        SDProductDetailsPageBtnClick?.('show in inches pic');
                        setShowDimensions(!showDimensions);
                      }}
                    >
                      {isWebGoodsDetail && (
                        <div className="w-[36px] h-[36px] mr-8 rounded-full bg-white border border-[#d4d4d4] flex items-center justify-center">
                          <Image
                            src="/goods-detail/size.png"
                            alt="frame-width"
                            width={36}
                            height={36}
                            quality={100}
                            className="w-20 h-auto"
                          />
                        </div>
                      )}
                      <div>
                        {showDimensions
                          ? t('mainPicture.dimensions.hide')
                          : t('mainPicture.dimensions.show')}
                      </div>
                    </Box>
                  )}
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* 左右切换箭头按钮 */}
          {isWebGoodsDetail && (
            <SwiperNavigationButtons
              navigationPrefix="main-picture"
              prevButtonPosition="left-[20px]"
              nextButtonPosition="right-[20px]"
              iconStyle={{ fontSize: '13px' }}
            />
          )}

          {!isWebGoodsDetail && (
            <Box
              className="custom-pagination"
              sx={{
                '&.custom-pagination': {
                  position: 'absolute',
                  top: isGoodsDetail ? '14px !important' : 'auto !important',
                  bottom: isGoodsDetail ? 'auto !important' : '4vw !important',
                  left: showTabBtn ? '40vw !important' : '14px !important',
                  width: showTabBtn ? '35px !important' : '55px !important',
                  height: '6.4vw !important',
                  bgcolor: showTabBtn ? 'transparent' : 'rgba(0,0,0,0.3)',
                  borderRadius: showTabBtn ? 0 : '14px',
                  textAlign: 'center',
                  lineHeight: '6.4vw',
                  color: 'white !important',
                  zIndex: isGoodsDetail ? 12 : 10,
                  fontSize: '3.2vw',
                },
                '& .swiper-pagination-current': {
                  color: 'white',
                  fontSize: '3.2vw',
                },
                '& .swiper-pagination-total': {
                  color: 'white',
                  fontSize: '3.2vw',
                },
              }}
            />
          )}
        </Box>

        {!isWebGoodsDetail ? (
          <ImagePreview
            ref={imagePreviewRef}
            images={images.filter((item) => item.goodsType !== 'video').map((item) => item.url)}
            showThumbnail={false}
          />
        ) : null}

        {/* Div 悬浮预览窗口 - 参考Vue版本实现 */}
        {isClient &&
          isWebGoodsDetail &&
          document.querySelector('.goods-detail-main-picture') &&
          createPortal(
            <div
              key={`zoom-preview-${forceUpdate}`}
              className="zoom-preview border border-gray-300 bg-white shadow-xl pointer-events-none overflow-hidden"
              style={{
                ...getDivZoomWindowPosition(),
                width: `${getZoomWindowSize()}px`,
                height: `${getZoomWindowSize()}px`,
                borderWidth: ZOOM_PREVIEW_BORDER_WIDTH,
                display: isShowZoomPop ? 'block' : 'none',
              }}
            >
              <div style={getDivZoomStyle()}>
                {/* 使用img元素，参考Vue版本的实现 */}
                <img
                  src={images[activeIndex]?.url}
                  alt="zoom preview"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    // 与左侧 MediaItem 保持相同的图片裁剪方式。
                    objectFit: 'cover',
                    maxWidth: 'none',
                    maxHeight: 'none',
                    ...(() => {
                      const { left, top } = getHoverBoxPosition();
                      const dimensions = initZoomDimensions();
                      if (!dimensions) return { transform: 'translate(0, 0)' };

                      const { hoverWidth, hoverHeight, pWidth, pHeight, bWidth, bHeight } =
                        dimensions;

                      // 将选框完整映射到预览窗口：尺寸和位移必须使用同一缩放比例。
                      const scaleX = bWidth / hoverWidth;
                      const scaleY = bHeight / hoverHeight;

                      return {
                        width: `${pWidth * scaleX}px`,
                        height: `${pHeight * scaleY}px`,
                        transform: `translate(${-left * scaleX}px, ${-top * scaleY}px)`,
                      };
                    })(),
                    // userSelect: 'none',
                    // pointerEvents: 'none',
                  }}
                />
              </div>
            </div>,
            document.querySelector('.goods-detail-main-picture') as Element,
          )}
      </div>
    );
  },
);
MainPicture.displayName = 'MainPicture';
export default MainPicture;
